import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import type { OrderStatus, PaymentMethod } from '../domain/order';
import { RUNTIME_CONFIG } from '../runtime-config';

export interface HealthResponse {
  readonly status: string;
  readonly env: string;
  readonly database: {
    readonly reachable: boolean;
    readonly version: string | null;
    readonly latencyMs: number;
  };
  readonly timestamp: string;
}

export interface MeResponse {
  readonly id: string;
  /**
   * Nulle par construction sur le jeton d'accès : seul le jeton d'identité
   * porte l'adresse. Le front la complète alors depuis la session locale.
   */
  readonly email: string | null;
  readonly groups: readonly string[];
  readonly fullName: string | null;
  readonly phone: string | null;
  readonly street: string | null;
  readonly addressLine2: string | null;
  readonly postalCode: string | null;
  readonly commune: string | null;
  readonly photoUrl: string | null;
}

export interface MeUpdate {
  readonly fullName: string;
  readonly phone: string | null;
  readonly street: string | null;
  readonly addressLine2: string | null;
  readonly postalCode: string | null;
  readonly commune: string | null;
  readonly photoUrl: string | null;
  readonly referrerUserId?: string | null;
}

export interface ClientAchievementDto {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly unlocked: boolean;
  readonly unlockedAt: string | null;
}

export interface ClientVipPerkDto {
  readonly id: string;
  readonly label: string;
}

export interface ClientAchievementsResponse {
  readonly achievements: readonly ClientAchievementDto[];
  readonly vip: readonly ClientVipPerkDto[];
}

export type FavoriteAvailability = 'available' | 'out_of_stock' | 'unavailable';

export interface FavoriteProducerDto {
  readonly id: string;
  readonly name: string;
  readonly commune: string;
  readonly createdAt: string;
}

export interface FavoriteProductDto {
  readonly id: string;
  readonly name: string;
  readonly producerId: string;
  readonly producerName: string;
  readonly commune: string;
  readonly unit: string;
  readonly priceCents: number;
  readonly quantity: number;
  readonly availability: FavoriteAvailability;
  readonly createdAt: string;
}

export interface ClientFavoritesResponse {
  readonly producers: readonly FavoriteProducerDto[];
  readonly products: readonly FavoriteProductDto[];
}

export interface CartLinePayloadDto {
  readonly lineKey: string;
  readonly productId: string;
  readonly productName: string;
  readonly quantity: number;
  readonly priceType: string;
  readonly unit: string;
  readonly unitPriceCents: number;
  readonly antiWastePercent?: number;
  readonly extraIds: readonly string[];
  readonly extrasLabel?: string | null;
  readonly extraCents?: number;
  readonly baseUnitPriceCents?: number;
  readonly quantityStep?: number;
  readonly minQuantity?: number;
  readonly quantityBreaks?: readonly QuantityBreakDto[];
}

export interface CartPayloadDtoV1 {
  readonly version: 1;
  readonly producerId: string;
  readonly producerName: string;
  readonly commune: string;
  readonly cardAccepted: boolean;
  readonly lines: readonly CartLinePayloadDto[];
  readonly updatedAt: string;
}

export interface CartPayloadDtoV2 {
  readonly version: 2;
  readonly blocks: readonly {
    readonly producerId: string;
    readonly producerName: string;
    readonly commune: string;
    readonly cardAccepted: boolean;
    readonly lines: readonly CartLinePayloadDto[];
  }[];
  readonly updatedAt: string;
}

export type CartPayloadDto = CartPayloadDtoV1 | CartPayloadDtoV2;

/** @deprecated Alias v1 — conservé pour compatibilité. */
export interface CartPayloadDtoLegacy {
  readonly version: 1;
  readonly producerId: string;
  readonly producerName: string;
  readonly commune: string;
  readonly cardAccepted: boolean;
  readonly lines: readonly CartLinePayloadDto[];
  readonly updatedAt: string;
}

export interface SavedCartTemplateSummaryDto {
  readonly id: string;
  readonly label: string;
  readonly producerId: string;
  readonly producerName: string;
  readonly commune: string;
  readonly lineCount: number;
  readonly updatedAt: string;
}

export interface SavedCartTemplateDetailDto extends SavedCartTemplateSummaryDto {
  readonly cart: CartPayloadDto;
}

export interface PersonalImpactDto {
  readonly pickedUpOrders: number;
  readonly antiWasteOrders: number;
  readonly distinctProducers: number;
  readonly estimatedSavingsCents: number;
}

export interface NotificationPreference {
  readonly type: string;
  readonly push: boolean;
  readonly email: boolean;
}

export interface DndSettings {
  readonly enabled: boolean;
  readonly startsAt: string;
  readonly endsAt: string;
}

export interface NotificationSettings {
  readonly preferences: readonly NotificationPreference[];
  readonly dnd: DndSettings;
}

/** Établissement tel que le répertoire Sirene le décrit. */
export interface SireneEstablishment {
  readonly siret: string;
  readonly siren: string;
  /** Nulle pour un établissement non diffusible : le producteur la saisit alors. */
  readonly companyName: string | null;
  readonly tradeName: string | null;
  readonly legalFormCode: string | null;
  readonly legalForm: string | null;
  readonly nafCode: string | null;
  readonly address: string | null;
  readonly postalCode: string | null;
  readonly commune: string | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly isHeadOffice: boolean;
  readonly closedOn: string | null;
}

export type SireneOutcome = 'found' | 'closed' | 'not_found' | 'malformed' | 'unavailable';

/**
 * Réponse de la consultation d'un SIRET, toujours en HTTP 200.
 *
 * Un SIRET inconnu ou un annuaire injoignable ne sont pas des erreurs de la
 * requête : ce sont les réponses de l'annuaire, et le formulaire les affiche
 * toutes de la même manière.
 */
export interface SireneLookupResponse {
  readonly outcome: SireneOutcome;
  readonly message: string | null;
  readonly establishment: SireneEstablishment | null;
  /** Vrai uniquement si l'établissement existe et est ouvert. */
  readonly usable: boolean;
}

export type ProducerStatus =
  'pending_siret' | 'siret_verified' | 'payment_pending' | 'active' | 'rejected';

/**
 * Rôles au sein d'une exploitation.
 *
 * Un co-titulaire engage l'entreprise, un vendeur tient la gamme et le stand, un
 * renfort ne fait que servir.
 */
export type StaffRole = 'producteur' | 'vendeur' | 'staff';

export type StaffStatus = 'invited' | 'active' | 'revoked';

/** Actions soumises à permission, telles que le serveur les nomme. */
export type StaffAction =
  | 'shop.read'
  | 'product.write'
  | 'pickup.write'
  | 'order.read'
  | 'order.confirm_pickup'
  | 'legal.read'
  | 'staff.manage'
  | 'payment.manage'
  | 'review.read'
  | 'review.reply'
  | 'stats.read'
  | 'margin.read'
  | 'margin.manage';

export interface ProducerProfileDto {
  readonly id: string;
  /**
   * Nuls quand le rôle du compte ne donne pas accès aux données légales — c'est
   * le serveur qui les retire, `legalRedacted` disant alors pourquoi le champ est
   * vide. Un gabarit qui masquerait le champ ne masquerait rien : la donnée
   * serait déjà partie sur le réseau.
   */
  readonly siret: string | null;
  readonly siren: string | null;
  readonly companyName: string;
  readonly legalFormCode: string | null;
  readonly legalForm: string | null;
  readonly nafCode: string | null;
  readonly address: string | null;
  readonly postalCode: string | null;
  readonly commune: string | null;
  readonly professionalPhone: string | null;
  readonly professionalEmail: string | null;
  readonly acceptsCardOnsite: boolean;
  readonly productCategories: readonly string[];
  readonly status: ProducerStatus;
  readonly rejectionReason: string | null;
  readonly siretVerifiedAt: string | null;
  readonly activatedAt: string | null;
  readonly manualReviewRequired: boolean;
  readonly isDemo: boolean;
  readonly photoUrl: string | null;
  readonly description: string | null;
  readonly legalRedacted: boolean;
}

/**
 * Droits calculés par le serveur à partir du statut.
 *
 * Le front les affiche sans les recalculer : dupliquer la règle côté navigateur
 * la ferait dériver, et une divergence sur `publiclyVisible` se paierait en
 * boutique publiée avant tout contrôle d'identité.
 */
export interface ProducerCapabilities {
  readonly canPrepareShop: boolean;
  readonly canAcceptOrders: boolean;
  readonly publiclyVisible: boolean;
  readonly needsPaymentOnboarding: boolean;
  /** Statut suffisant *et* au moins un point de retrait exploitable. */
  readonly canPublishProducts: boolean;
}

/** Rôle du compte sur l'exploitation, et les actions qu'il autorise. */
export interface ProducerMembership {
  readonly role: StaffRole;
  readonly actions: readonly StaffAction[];
}

export interface ProducerInvitation {
  readonly profileId: string;
  readonly companyName: string;
  readonly commune: string | null;
  readonly role: StaffRole;
  readonly invitedAt: string;
}

export interface ProducerProfileResponse {
  /** Nul pour un utilisateur qui n'a pas encore entamé le parcours. */
  readonly profile: ProducerProfileDto | null;
  readonly capabilities: ProducerCapabilities | null;
  readonly membership: ProducerMembership | null;
  /**
   * Invitations en attente. Présentes même sans profil : un compte peut être
   * invité sur une ferme sans en avoir une à lui, et n'aurait alors aucun autre
   * endroit où découvrir l'invitation.
   */
  readonly invitations: readonly ProducerInvitation[];
  readonly message: string | null;
}

export interface ProducerOnboardingRequest {
  readonly siret: string;
  readonly professionalPhone: string | null;
  readonly professionalEmail: string | null;
  readonly productCategories: readonly string[];
  readonly acceptsCardOnsite: boolean;
  readonly cguAccepted: boolean;
  readonly companyName: string | null;
  readonly legalForm: string | null;
  readonly address: string | null;
  readonly postalCode: string | null;
  readonly commune: string | null;
  readonly nafCode: string | null;
  readonly photoUrl: string | null;
  readonly description: string | null;
}

export interface ProducerPaymentResponse {
  /** Formulaire hébergé par le prestataire : ni IBAN ni pièce d'identité ne passent par nous. */
  readonly onboardingUrl: string;
  readonly profile: ProducerProfileDto;
  readonly capabilities: ProducerCapabilities;
}

export type PickupPointType = 'farm' | 'market' | 'other';

export type SlotRecurrence = 'weekly' | 'one_off';

export interface PickupSlotDto {
  readonly id: string;
  /** 1 = lundi … 7 = dimanche. Nul pour un créneau ponctuel. */
  readonly dayOfWeek: number | null;
  /** Nulle pour un créneau hebdomadaire. */
  readonly specificDate: string | null;
  /** « HH:MM », heure locale de l'exploitation. */
  readonly startTime: string;
  readonly endTime: string;
  readonly recurrence: SlotRecurrence;
  readonly active: boolean;
}

export interface PickupPointDto {
  readonly id: string;
  readonly label: string;
  readonly type: PickupPointType;
  readonly address: string | null;
  readonly postalCode: string | null;
  readonly commune: string | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly active: boolean;
  readonly slots: readonly PickupSlotDto[];
}

/** Créneau tel qu'on l'envoie : sans identifiant, il n'existe pas encore. */
export type PickupSlotRequest = Omit<PickupSlotDto, 'id'>;

export interface PickupPointRequest extends Omit<PickupPointDto, 'id' | 'slots'> {
  readonly slots: readonly PickupSlotRequest[];
}

export interface PickupPointsResponse {
  readonly points: readonly PickupPointDto[];
  readonly canPublishProducts: boolean;
}

export interface PickupPointResponse {
  readonly point: PickupPointDto;
}

export interface StaffMemberDto {
  readonly userId: string;
  readonly role: StaffRole;
  readonly status: StaffStatus;
  readonly invitedBy: string | null;
  readonly joinedAt: string | null;
  readonly createdAt: string;
  /** Nul si le compte invité n'a pas encore rempli son profil client. */
  readonly fullName: string | null;
}

export interface SeatAllowance {
  readonly free: number;
  readonly purchased: number;
  readonly total: number;
  readonly occupied: number;
  readonly remaining: number;
  readonly canBuyMore: boolean;
}

export interface ProducerTeamResponse {
  readonly members: readonly StaffMemberDto[];
  readonly seats: SeatAllowance;
  readonly producteurSeats: { readonly occupied: number; readonly max: number };
  readonly absoluteMaxStaffSeats: number;
  readonly tierLevel: number;
  /** Le compte connecté, pour ne pas lui proposer de se retirer lui-même. */
  readonly selfUserId: string;
}

export interface ProducerTierDto {
  readonly appliedLevel: number;
  readonly appliedTitle: string;
  readonly revenueLevel: number;
  readonly revenueTitle: string;
  readonly monthlyRevenueCents: number;
  readonly subscriptionCents: number;
  readonly standardSubscriptionCents: number;
  readonly boosted: boolean;
  readonly maxPhotosPerProduct: number | null;
  readonly maxActiveProducts: number | null;
  readonly flagshipFeature: string;
  readonly canDowngrade: boolean;
  readonly downgradeTargetLevel: number | null;
  readonly pendingDowngradeLevel: number | null;
  readonly pendingDowngradeAt: string | null;
  readonly downgradeWarning: DowngradeWarningDto | null;
  readonly boostOffers: readonly TierBoostOfferDto[];
}

export interface DowngradeWarningDto {
  readonly targetLevel: number;
  readonly targetTitle: string;
  readonly effectiveAt: string;
  readonly subscriptionCents: number;
  readonly productCap: number | null;
  readonly photoCap: number | null;
  readonly publishedToArchive: number;
  readonly lostFeatures: readonly string[];
}

export interface TierBoostOfferDto {
  readonly level: number;
  readonly title: string;
  readonly standardCents: number;
  readonly boostCents: number;
  readonly revenueFromCents: number;
  readonly flagshipFeature: string;
}

export interface InviteStaffRequest {
  readonly email: string;
  readonly role: StaffRole;
}

export type ProductPriceType = 'kg' | 'piece' | 'litre' | 'botte' | 'bouteille';
export type ProductStatus = 'draft' | 'published' | 'archived';
export type AntiWasteKind = 'visual_defect' | 'short_date' | 'overproduction';

export interface ProductPhotoDto {
  readonly id: string;
  readonly url: string;
  readonly sortOrder: number;
}

export interface ProductDto {
  readonly id: string;
  readonly producerProfileId: string;
  readonly name: string;
  readonly description?: string | null;
  readonly category: string;
  readonly priceType: ProductPriceType;
  readonly priceCents: number;
  readonly bottleVolumeLitres?: number | null;
  readonly quantity: number;
  readonly unlimitedStock?: boolean;
  readonly status: ProductStatus;
  readonly photos: readonly ProductPhotoDto[];
  readonly extras?: readonly ProductExtraDto[];
  readonly quantityStep?: number;
  readonly minQuantity?: number;
  readonly quantityBreaks?: readonly QuantityBreakDto[];
  readonly videoUrl?: string | null;
  readonly antiWasteKind: AntiWasteKind | null;
  readonly antiWasteExpiresAt: string | null;
  readonly antiWastePercent?: number;
  readonly antiWastePriceCents?: number | null;
  readonly materialCostCents?: number | null;
  readonly workMinutes?: number | null;
  readonly packagingCostCents?: number | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ProductLimits {
  readonly tierLevel: number;
  readonly maxPhotosPerProduct: number | null;
  readonly maxActiveProducts: number | null;
  readonly canVideo?: boolean;
  readonly canExtras?: boolean;
  readonly canMargin?: boolean;
}

export interface ProductExtraDto {
  readonly id: string;
  readonly label: string;
  readonly priceCents: number;
  readonly sortOrder?: number;
}

export interface QuantityBreakDto {
  readonly fromQuantity: number;
  readonly kind: 'percent' | 'amount';
  readonly percent: number | null;
  readonly amountCents: number | null;
}

export interface ProductsResponse {
  readonly products: readonly ProductDto[];
  readonly publishedCount: number;
  readonly limits: ProductLimits;
  readonly canPublishProducts: boolean;
  readonly canPrepareShop: boolean;
}

export interface ProductRequest {
  readonly name: string;
  readonly description?: string | null;
  readonly category: string;
  readonly priceType: ProductPriceType;
  readonly priceCents: number;
  readonly bottleVolumeLitres?: number | null;
  readonly quantity: number;
  readonly unlimitedStock?: boolean;
  readonly status: ProductStatus;
  readonly photoUrls: readonly string[];
  readonly extras?: readonly { readonly label: string; readonly priceCents: number }[];
  readonly quantityStep?: number | null;
  readonly minQuantity?: number | null;
  readonly quantityBreaks?: readonly QuantityBreakDto[];
  readonly videoUrl?: string | null;
  readonly antiWasteKind?: AntiWasteKind | null;
  readonly antiWasteExpiresAt?: string | null;
  readonly antiWastePercent?: number | null;
  readonly antiWastePriceCents?: number | null;
  readonly materialCostCents?: number | null;
  readonly workMinutes?: number | null;
  readonly packagingCostCents?: number | null;
}

export interface ProductResponse {
  readonly product: ProductDto;
}

export interface MediaUploadResponse {
  readonly uploadUrl: string;
  readonly publicUrl: string;
  readonly maxBytes: number;
  readonly contentType: string;
  readonly kind?: 'photo' | 'video';
}

export type AdminPeriodId = '7j' | '30j' | '90j' | '12m' | 'tout';

/** Indicateur et son antécédent : `previous` est nul quand la comparaison est impossible. */
export interface AdminMetricDto {
  readonly value: number;
  readonly previous: number | null;
}

export interface AdminOfferSummaryDto {
  readonly publishedProducts: number;
  readonly draftProducts: number;
  readonly archivedProducts: number;
  readonly outOfStock: number;
  readonly withoutPhoto: number;
  readonly antiWasteActive: number;
  readonly withoutPlannedStock: number;
  readonly plannedUnitsRemaining: number;
  readonly categoriesCovered: number;
  readonly producersWithOffer: number;
}

/** Point d'une série temporelle (jour, semaine ou mois selon la période). */
export interface AdminSeriesPointDto {
  readonly bucket: string;
  readonly value: number;
}

export interface AdminOverviewDto {
  readonly period: {
    readonly id: string;
    readonly label: string;
    readonly from: string;
    readonly to: string;
    readonly comparable: boolean;
  };
  readonly producers: {
    readonly total: number;
    readonly active: number;
    readonly pendingActivation: number;
    readonly activationRate: number;
    readonly created: AdminMetricDto;
    readonly selling: AdminMetricDto;
    readonly dormant: number;
    readonly withoutPublishedProduct: number;
    readonly withoutUsablePickup: number;
    readonly awaitingManualReview: number;
    readonly byStatus: Readonly<Record<string, number>>;
    readonly byTier: Readonly<Record<string, number>>;
    readonly boosted: number;
    readonly pendingDowngrade: number;
  };
  readonly offer: AdminOfferSummaryDto;
  readonly demand: {
    readonly orders: AdminMetricDto;
    readonly gmvCents: AdminMetricDto;
    readonly averageBasketCents: AdminMetricDto;
    readonly activeClients: AdminMetricDto;
    readonly newClients: AdminMetricDto;
    readonly totalClients: number;
    readonly repeatRate: number;
    readonly onlinePaymentRate: number;
    readonly guestOrderRate: number;
    readonly gmvOnlineCents: number;
    readonly gmvOnsiteCents: number;
  };
  readonly revenue: {
    readonly commissionCents: AdminMetricDto;
    readonly subscriptionCents: AdminMetricDto;
    readonly totalCents: AdminMetricDto;
    readonly takeRate: number;
    readonly arpuCents: number;
    readonly carriedBalanceCents: number;
    readonly producersCarrying: number;
    readonly sepaCandidates: number;
  };
  readonly execution: {
    readonly pickedUp: number;
    readonly noShow: number;
    readonly cancelled: number;
    readonly pendingPayment: number;
    readonly pickupRate: number;
    readonly noShowRate: AdminMetricDto;
    readonly cancellationRate: number;
    readonly averageDistanceKm: number;
    readonly ordersWithDistance: number;
  };
  readonly quality: {
    readonly publishedReviews: AdminMetricDto;
    readonly averageRating: number;
    readonly pendingHumanReviews: number;
    readonly reviewRate: number;
    readonly shortageEvents: AdminMetricDto;
    readonly unsatisfiedSearches: AdminMetricDto;
    readonly clientsAtRisk: number;
  };
  readonly trend: readonly {
    readonly month: string;
    readonly orders: number;
    readonly gmvCents: number;
    readonly activeClients: number;
  }[];
  readonly series?: Readonly<Record<string, readonly AdminSeriesPointDto[]>>;
  readonly seriesGranularity?: 'day' | 'week' | 'month';
  readonly sectors: readonly {
    readonly sector: string;
    readonly producers: number;
    readonly publishedProducts: number;
    readonly orders: number;
    readonly gmvCents: number;
    readonly clients: number;
  }[];
}

export type AdminAlertSeverity = 'critical' | 'warning' | 'info';

export interface AdminAlertDto {
  readonly id: string;
  readonly severity: AdminAlertSeverity;
  readonly category: string;
  readonly title: string;
  readonly detail: string;
  readonly count: number;
  readonly targetKind: 'producer' | 'client' | 'product' | 'review' | 'order' | 'sector' | null;
  readonly targets: readonly {
    readonly id: string;
    readonly label: string;
    readonly hint: string | null;
  }[];
  readonly action: string;
}

export interface AdminAlertsDto {
  readonly generatedAt: string;
  readonly criticalCount: number;
  readonly warningCount: number;
  readonly infoCount: number;
  readonly alerts: readonly AdminAlertDto[];
}

export interface AdminProductGapDto {
  readonly productId: string;
  readonly productName: string;
  readonly category: string;
  readonly producerId: string;
  readonly producerName: string;
  readonly priceCents: number;
  readonly reason: string;
}

export interface AdminCatalogDto {
  readonly summary: AdminOfferSummaryDto;
  readonly byCategory: readonly {
    readonly category: string;
    readonly publishedProducts: number;
    readonly draftProducts: number;
    readonly sellingProducers: number;
    readonly declaringProducers: number;
    readonly outOfStock: number;
    readonly withoutPhoto: number;
    readonly antiWasteActive: number;
    readonly medianPriceCents: number;
    readonly unitsSold: number;
    readonly revenueCents: number;
    readonly shortageEvents: number;
  }[];
  readonly coverage: readonly {
    readonly sector: string;
    readonly producers: number;
    readonly publishedProducts: number;
    readonly coveredCategories: readonly string[];
    readonly missingCategories: readonly string[];
  }[];
  readonly neverSold: readonly AdminProductGapDto[];
  readonly qualityGaps: readonly AdminProductGapDto[];
  readonly staleDrafts: readonly AdminProductGapDto[];
}

export interface AdminProducerSummaryDto {
  readonly id: string;
  readonly companyName: string;
  readonly commune: string | null;
  readonly status: string;
  readonly isDemo: boolean;
  readonly tierLevel: number;
  readonly tierBoosted: boolean;
  readonly categories: readonly string[];
  readonly createdAt: string;
  readonly activatedAt: string | null;
  readonly manualReviewRequired: boolean;
  readonly manualReviewDoneAt: string | null;
  readonly publishedProducts: number;
  readonly draftProducts: number;
  readonly outOfStockProducts: number;
  readonly ordersInPeriod: number;
  readonly revenueInPeriodCents: number;
  readonly lastOrderAt: string | null;
  readonly hasUsablePickup: boolean;
  readonly upcomingOccurrences: number;
  readonly reviewCount: number;
  readonly averageRating: number | null;
  readonly carriedBalanceCents: number;
  readonly healthScore: number;
  readonly healthIssues: readonly string[];
}

export interface AdminProducerProductDto {
  readonly id: string;
  readonly name: string;
  readonly category: string;
  readonly status: string;
  readonly priceType: string;
  readonly priceCents: number;
  readonly quantity: number;
  readonly unlimitedStock?: boolean;
  readonly photoCount: number;
  readonly extrasCount: number;
  readonly quantityBreaksCount: number;
  readonly hasVideo: boolean;
  readonly hasCostData: boolean;
  readonly antiWasteKind: string | null;
  readonly plannedRemaining: number;
  readonly unitsSold: number;
  readonly revenueCents: number;
  readonly ordersCount: number;
  readonly lastSoldAt: string | null;
  readonly reviewCount: number;
  readonly averageRating: number | null;
  readonly createdAt: string;
}

export interface AdminProducerGapDto {
  readonly code: string;
  readonly severity: 'blocking' | 'important' | 'opportunity';
  readonly label: string;
  readonly detail: string;
  readonly action: string;
}

export interface AdminProducerDetailDto {
  readonly summary: AdminProducerSummaryDto;
  readonly identity: {
    readonly siret: string;
    readonly siren: string;
    readonly legalForm: string | null;
    readonly nafCode: string | null;
    readonly address: string | null;
    readonly postalCode: string | null;
    readonly professionalPhone: string | null;
    readonly professionalEmail: string | null;
    readonly acceptsCardOnsite: boolean;
    readonly paymentProvider: string | null;
    readonly siretVerifiedAt: string | null;
    readonly cguAcceptedAt: string | null;
    readonly rejectionReason: string | null;
    readonly latitude: number | null;
    readonly longitude: number | null;
  };
  readonly tier: {
    readonly appliedLevel: number;
    readonly appliedTitle: string;
    readonly revenueLevel: number;
    readonly monthlyRevenueCents: number;
    readonly subscriptionCents: number;
    readonly boosted: boolean;
    readonly pendingDowngradeLevel: number | null;
    readonly pendingDowngradeAt: string | null;
    readonly maxActiveProducts: number | null;
    readonly maxPhotosPerProduct: number;
    readonly publishedProducts: number;
    readonly atProductCap: boolean;
  };
  readonly range: readonly AdminProducerProductDto[];
  readonly gaps: readonly AdminProducerGapDto[];
  readonly declaredCategoriesWithoutOffer: readonly string[];
  readonly untappedCategories: readonly string[];
  readonly pickup: {
    readonly points: readonly {
      readonly id: string;
      readonly label: string;
      readonly type: string;
      readonly commune: string | null;
      readonly active: boolean;
      readonly activeSlots: number;
      readonly upcomingOccurrences: number;
      readonly plannedUnits: number;
    }[];
    readonly upcomingOccurrences: number;
    readonly plannedUnitsRemaining: number;
  };
  readonly commerce: {
    readonly monthly: readonly {
      readonly month: string;
      readonly orders: number;
      readonly revenueCents: number;
      readonly noShow: number;
      readonly units: number;
    }[];
    readonly ordersByStatus: Readonly<Record<string, number>>;
    readonly distinctClients: number;
    readonly loyalClients: number;
    readonly guestOrders: number;
    readonly averageBasketCents: number;
    readonly averageDistanceKm: number | null;
    readonly peakWeekday: number | null;
    readonly revenueAllTimeCents: number;
  };
  readonly clients: readonly {
    readonly userId: string;
    readonly fullName: string;
    readonly commune: string | null;
    readonly orders: number;
    readonly totalCents: number;
    readonly lastOrderAt: string | null;
  }[];
  readonly finance: {
    readonly carriedBalanceCents: number;
    readonly carriedSince: string | null;
    readonly payoutFrequency: string | null;
    readonly movements: readonly {
      readonly kind: string;
      readonly amountCents: number;
      readonly note: string | null;
      readonly createdAt: string;
    }[];
    readonly payouts: readonly {
      readonly periodStart: string;
      readonly periodEnd: string;
      readonly grossCents: number;
      readonly commissionCents: number;
      readonly subscriptionCents: number;
      readonly netCents: number;
      readonly status: string;
    }[];
  };
  readonly reputation: {
    readonly averageRating: number | null;
    readonly reviewCount: number;
    readonly pendingReviews: number;
    readonly repliedRate: number;
    readonly achievements: readonly { readonly id: string; readonly unlockedAt: string }[];
    readonly activeBoosters: readonly {
      readonly source: string;
      readonly productId: string | null;
      readonly endsAt: string;
      readonly sponsored: boolean;
    }[];
    readonly recentReviews: readonly {
      readonly id: string;
      readonly rating: number;
      readonly comment: string;
      readonly status: string;
      readonly productName: string;
      readonly createdAt: string;
      readonly hasReply: boolean;
    }[];
  };
  readonly staff: readonly {
    readonly userId: string;
    readonly role: string;
    readonly status: string | null;
    readonly joinedAt: string | null;
  }[];
}

export type AdminClientSegment =
  | 'sans_commande'
  | 'nouveau'
  | 'actif'
  | 'fidele'
  | 'endormi'
  | 'perdu';

export interface AdminClientSummaryDto {
  readonly userId: string;
  readonly fullName: string;
  readonly commune: string | null;
  readonly postalCode: string | null;
  readonly photoUrl: string | null;
  readonly createdAt: string;
  readonly orderCount: number;
  readonly totalSpentCents: number;
  readonly firstOrderAt: string | null;
  readonly lastOrderAt: string | null;
  readonly ordersInPeriod: number;
  readonly spentInPeriodCents: number;
  readonly noShowCount: number;
  readonly requiresOnlinePayment: boolean;
  readonly reliabilityScore: number;
  readonly distinctProducers: number;
  readonly favoriteCount: number;
  readonly achievementCount: number;
  readonly reviewCount: number;
  readonly segment: AdminClientSegment;
}

export interface AdminClientsViewDto {
  readonly clients: readonly AdminClientSummaryDto[];
  readonly segments: Readonly<Record<AdminClientSegment, number>>;
  readonly guests: readonly {
    readonly email: string;
    readonly orders: number;
    readonly totalCents: number;
    readonly lastOrderAt: string;
    readonly hasAccount: boolean;
  }[];
}

export interface AdminClientDetailDto {
  readonly summary: AdminClientSummaryDto;
  readonly profile: {
    readonly phone: string | null;
    readonly street: string | null;
    readonly addressLine2: string | null;
    readonly referrerUserId: string | null;
    readonly referrerName: string | null;
    readonly sectorDigestSentAt: string | null;
  };
  readonly behaviour: {
    readonly ordersByStatus: Readonly<Record<string, number>>;
    readonly onlinePaymentRate: number;
    readonly paymentMix: Readonly<Record<string, number>>;
    readonly orderFrequencyDays: number | null;
    readonly averageBasketCents: number;
    readonly averageDistanceKm: number | null;
    readonly antiWasteLines: number;
    readonly monthly: readonly {
      readonly month: string;
      readonly orders: number;
      readonly spentCents: number;
    }[];
  };
  readonly revenueByProducer: readonly {
    readonly producerId: string;
    readonly producerName: string;
    readonly orders: number;
    readonly totalCents: number;
    readonly lastOrderAt: string | null;
  }[];
  readonly preferredCategories: readonly {
    readonly category: string;
    readonly lines: number;
    readonly spentCents: number;
  }[];
  readonly recurringProducts: readonly {
    readonly productName: string;
    readonly orderCount: number;
    readonly units: number;
  }[];
  readonly orders: readonly {
    readonly id: string;
    readonly producerName: string;
    readonly status: string;
    readonly paymentMethod: string;
    readonly totalCents: number;
    readonly itemCount: number;
    readonly distanceKm: number | null;
    readonly pickupStartsAt: string | null;
    readonly createdAt: string;
  }[];
  readonly engagement: {
    readonly favoriteProducers: readonly { readonly id: string; readonly label: string }[];
    readonly favoriteProducts: readonly { readonly id: string; readonly label: string }[];
    readonly savedCartTemplates: readonly {
      readonly id: string;
      readonly label: string;
      readonly producerName: string | null;
      readonly updatedAt: string;
    }[];
    readonly activeCartProducerName: string | null;
    readonly activeCartUpdatedAt: string | null;
    readonly achievements: readonly { readonly id: string; readonly unlockedAt: string }[];
    readonly vipPerks: readonly string[];
    readonly notificationPreferences: readonly {
      readonly type: string;
      readonly push: boolean;
      readonly email: boolean;
    }[];
    readonly doNotDisturb: {
      readonly enabled: boolean;
      readonly startsAt: string;
      readonly endsAt: string;
    } | null;
  };
  readonly reviews: readonly {
    readonly id: string;
    readonly rating: number;
    readonly comment: string;
    readonly status: string;
    readonly productName: string | null;
    readonly producerName: string | null;
    readonly createdAt: string;
  }[];
  readonly referrals: {
    readonly referredCount: number;
    readonly referredActiveCount: number;
    readonly referred: readonly {
      readonly userId: string;
      readonly fullName: string;
      readonly createdAt: string;
      readonly hasOrdered: boolean;
    }[];
  };
}

export interface AdminStrategyDto {
  readonly hasEnoughData: boolean;
  readonly readiness: {
    readonly producers: number;
    readonly producersRequired: number;
    readonly clients: number;
    readonly clientsRequired: number;
  };
  readonly sectorSaturation: readonly {
    readonly sector: string;
    readonly producerCount: number;
    readonly clientCount: number;
  }[];
  readonly sectorCategoryGaps: readonly {
    readonly sector: string;
    readonly category: string;
    readonly clientDemand: number;
    readonly producerCount: number;
  }[];
  readonly unsatisfiedSearches: readonly {
    readonly query: string;
    readonly sector: string | null;
    readonly count: number;
  }[];
  readonly stockShortages: readonly {
    readonly category: string;
    readonly sector: string | null;
    readonly count: number;
  }[];
  readonly prioritySectors: readonly {
    readonly sector: string;
    readonly score: number;
    readonly reason: string;
  }[];
}

export interface AdminDistanceDto {
  readonly bySector: readonly {
    readonly sector: string;
    readonly count: number;
    readonly averageKm: number;
    readonly medianKm: number;
    readonly p25Km: number;
    readonly p75Km: number;
  }[];
  readonly overall: {
    readonly count: number;
    readonly averageKm: number;
    readonly medianKm: number;
    readonly p90Km: number;
  };
}

export interface AdminClusterDto {
  readonly id: string;
  readonly producerProfileIds: readonly string[];
  readonly producerNames: readonly string[];
  readonly sharedClientCount: number;
  readonly geographicScore: number;
  readonly opportunityScore: number;
  readonly suggestedLatitude: number | null;
  readonly suggestedLongitude: number | null;
  readonly computedAt: string;
}

export interface CatalogProductDto {
  readonly id: string;
  readonly name: string;
  readonly category: string;
  readonly priceCents: number;
  readonly unit: string;
  readonly antiWastePercent?: number;
  readonly sponsored?: boolean;
  readonly availableNextSlot?: boolean;
  readonly nextAvailabilityLabel?: string;
}

export interface CatalogProducerDto {
  readonly id: string;
  readonly name: string;
  readonly commune: string;
  readonly coordinates: { readonly latitude: number; readonly longitude: number };
  readonly destinations?: readonly {
    readonly id: string;
    readonly label: string;
    readonly latitude: number;
    readonly longitude: number;
  }[];
  readonly tierLevel: number;
  readonly reviewCount: number;
  readonly ratingAverage?: number;
  readonly pickup: string;
  readonly cardAccepted: boolean;
  readonly achievements: readonly string[];
  readonly products: readonly CatalogProductDto[];
}

export interface CatalogResponse {
  readonly producers: readonly CatalogProducerDto[];
}

export interface PublicShopSkinDto {
  readonly slug: string;
  readonly template: string;
  readonly accent: string | null;
  readonly heroUrl: string | null;
  readonly logoUrl: string | null;
  readonly motion: string;
  readonly seasonPack: string | null;
  readonly cssVars: Readonly<Record<string, string>>;
  readonly chrome: boolean;
  readonly whiteLabel: boolean;
}

export interface CatalogBoutiqueResponse {
  readonly producer: CatalogProducerDto;
  readonly shop: PublicShopSkinDto;
}

export interface ShopThemeDto {
  readonly slug: string;
  readonly template: string;
  readonly accent: string | null;
  readonly heroUrl: string | null;
  readonly logoUrl: string | null;
  readonly motion: string;
  readonly seasonPack: string | null;
  readonly domainKind: string;
  readonly domainStatus: string;
  readonly customHost: string | null;
}

export interface ShopCapabilitiesDto {
  readonly canTheme: boolean;
  readonly canAccent: boolean;
  readonly canHero: boolean;
  readonly canLogo: boolean;
  readonly canMotion: boolean;
  readonly canChrome: boolean;
  readonly canTypedSubdomain: boolean;
  readonly canMonthlySkin: boolean;
  readonly canWhiteLabel: boolean;
  readonly canCustomDomain: boolean;
}

export interface ShopTemplateOptionDto {
  readonly id: string;
  readonly label: string;
  readonly pitch: string;
  readonly suggested: boolean;
  readonly typedRoot: string | null;
  readonly accents: readonly { readonly id: string; readonly label: string; readonly value: string }[];
}

export interface ProducerShopResponse {
  readonly shop: ShopThemeDto | null;
  readonly capabilities: ShopCapabilitiesDto;
  readonly suggestedTemplateIds: readonly string[];
  readonly defaultSlug: string;
  readonly publicPath: string;
  readonly typedHost: string | null;
  readonly typedRoot: string | null;
  readonly appHost: string;
  readonly templates: readonly ShopTemplateOptionDto[];
}

export interface ProducerPosterSettingsDto {
  readonly photoUrl: string | null;
  readonly logoUrl?: string | null;
  readonly layout: string;
  readonly palette: string;
  readonly accent: string | null;
  readonly slogan?: string | null;
  readonly font?: string;
  readonly sloganInk?: string;
  readonly hideMark: boolean;
  readonly blocks?: readonly { readonly id: string; readonly on: boolean }[];
  readonly products?: readonly { readonly id: string; readonly label: string | null }[];
  readonly productsTitle?: string;
}

export interface PosterCapabilitiesDto {
  readonly canPalette: boolean;
  readonly canLayoutEtal: boolean;
  readonly canMetierPalette: boolean;
  readonly canSlogan: boolean;
  readonly canLayoutMarche: boolean;
  readonly canAccent: boolean;
  readonly canLogo: boolean;
  readonly canLayoutExpert: boolean;
  readonly canHideMark: boolean;
  readonly canWhiteLabel: boolean;
}

export interface PosterLayoutOptionDto {
  readonly id: string;
  readonly label: string;
  readonly pitch: string;
  readonly minTier: number;
  readonly locked: boolean;
}

export interface PosterPaletteOptionDto {
  readonly id: string;
  readonly label: string;
  readonly pitch: string;
  readonly minTier: number;
  readonly ink: string;
  readonly paper: string;
  readonly accent: string;
  readonly accents: readonly { readonly id: string; readonly label: string; readonly value: string }[];
  readonly locked: boolean;
}

export interface ProducerPosterResponse {
  readonly poster: ProducerPosterSettingsDto;
  readonly capabilities: PosterCapabilitiesDto;
  readonly logoUrl: string | null;
  readonly layouts: readonly PosterLayoutOptionDto[];
  readonly palettes: readonly PosterPaletteOptionDto[];
}

export interface ProducerPosterRequest {
  readonly photoUrl: string | null;
  readonly logoUrl: string | null;
  readonly layout: string;
  readonly palette: string;
  readonly accent: string | null;
  readonly slogan: string | null;
  readonly font: string;
  readonly sloganInk: string;
  readonly hideMark: boolean;
  readonly blocks: readonly { readonly id: string; readonly on: boolean }[];
  readonly products: readonly { readonly id: string; readonly label: string | null }[];
  readonly productsTitle: string;
}

export interface ProducerShopRequest {
  readonly slug: string;
  readonly template: string;
  readonly accent: string | null;
  readonly heroUrl: string | null;
  readonly logoUrl: string | null;
  readonly motion: string;
  readonly domainKind: string;
  readonly customHost: string | null;
}

export interface FeaturedSlotDto {
  readonly producer: {
    readonly id: string;
    readonly name: string;
    readonly commune: string;
  };
  readonly product: {
    readonly id: string;
    readonly name: string;
    readonly priceCents: number;
    readonly unit: string;
  };
  readonly sponsored: boolean;
}

export type BoosterKind = 'nudge' | 'spotlight';

export interface BoosterOfferDto {
  readonly kind: BoosterKind;
  readonly label: string;
  readonly description: string;
  readonly priceCents: number;
  readonly durationDays: number;
  readonly requiresProduct: boolean;
}

export interface ActiveBoosterDto {
  readonly kind: BoosterKind;
  readonly productId: string;
  readonly productName: string;
  readonly endsAt: string;
}

export interface ProducerBoostersResponse {
  readonly commercialized: boolean;
  readonly offers: readonly BoosterOfferDto[];
  readonly active: readonly ActiveBoosterDto[];
}

export interface PublicPickupSlotDto {
  readonly id: string;
  readonly label: string;
  readonly pickupStartsAt: string;
  readonly pickupEndsAt: string;
  readonly quantityRemaining?: number;
}

export interface PublicPickupPointDto {
  readonly id: string;
  readonly label: string;
  readonly address: string | null;
  readonly commune: string | null;
  readonly slots: readonly PublicPickupSlotDto[];
}

export interface ProductAvailabilityDto {
  readonly occurrenceId: string;
  readonly pickupStartsAt: string;
  readonly pickupEndsAt: string;
  readonly quantityRemaining: number;
  readonly label: string;
}

export interface ProducerAvailabilityRowDto {
  readonly occurrenceId: string;
  readonly pickupSlotId: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly quantityPlanned: number;
  readonly quantityRemaining: number;
}

export interface ProducerOccurrenceDto {
  readonly id: string;
  readonly pickupSlotId: string;
  readonly startsAt: string;
  readonly endsAt: string;
}

export interface ProductAvailabilitiesResponse {
  readonly productId: string;
  readonly unit: string;
  readonly availabilities: readonly ProducerAvailabilityRowDto[];
  readonly occurrences: readonly ProducerOccurrenceDto[];
}

export interface UpsertProductAvailabilityResponse {
  readonly availability: ProducerAvailabilityRowDto;
  readonly product?: ProductDto;
}

export interface ProductDetailResponse {
  readonly producer: {
    readonly id: string;
    readonly name: string;
    readonly commune: string;
    readonly cardAccepted: boolean;
  };
  readonly product: {
    readonly id: string;
    readonly name: string;
    readonly category: string;
    readonly priceType: string;
    readonly priceCents: number;
    readonly unit: string;
    readonly quantity: number;
    readonly unlimitedStock?: boolean;
    readonly photos: readonly string[];
    readonly extras?: readonly ProductExtraDto[];
    readonly quantityStep?: number;
    readonly minQuantity?: number;
    readonly quantityBreaks?: readonly QuantityBreakDto[];
    readonly videoUrl?: string;
    readonly antiWastePercent?: number;
  };
  readonly pickupPoints: readonly PublicPickupPointDto[];
  readonly availabilities?: readonly ProductAvailabilityDto[];
}

export interface OrderItemReviewDto {
  readonly id: string;
  readonly status: string;
  readonly rating: number;
}

export interface OrderItemDto {
  readonly id: string;
  readonly productId: string;
  readonly productName: string;
  readonly quantity: number;
  readonly priceType: string;
  readonly unitPriceCents: number;
  readonly lineTotalCents: number;
  readonly extrasLabel?: string | null;
  readonly review?: OrderItemReviewDto | null;
}

export interface OrderDto {
  readonly id: string;
  readonly orderNumber: string;
  readonly producerProfileId: string;
  readonly producerName: string | null;
  readonly pickupPointId: string;
  readonly pickupPointLabel: string | null;
  readonly pickupCommune: string | null;
  readonly pickupAddress: string | null;
  readonly pickupSlotId: string;
  readonly pickupStartsAt: string;
  readonly pickupEndsAt: string;
  readonly status: OrderStatus;
  readonly paymentMethod: PaymentMethod;
  readonly totalCents: number;
  readonly confirmToken: string;
  readonly pickedUpAt: string | null;
  readonly cancellationReason: string | null;
  readonly createdAt: string;
  readonly items: readonly OrderItemDto[];
  readonly providerPaymentId?: string | null;
}

export interface PickupSessionSummaryDto {
  readonly pickupPointId: string;
  readonly pickupPointLabel: string;
  readonly pickupSlotId: string;
  readonly pickupStartsAt: string;
  readonly pickupEndsAt: string;
  readonly plannedCount: number;
  readonly pickedUpCount: number;
  readonly noShowCount: number;
  readonly pendingCount: number;
}

export interface PreparationLineDto {
  readonly productId: string;
  readonly productName: string;
  readonly priceType: string;
  readonly totalQuantity: number;
  readonly orderCount: number;
}

export interface PickupSessionDetailDto {
  readonly session: {
    readonly pickupPointId: string;
    readonly pickupSlotId: string;
    readonly pickupStartsAt: string;
    readonly plannedCount: number;
    readonly pickedUpCount: number;
    readonly noShowCount: number;
    readonly pendingCount: number;
  };
  readonly orders: readonly OrderDto[];
  readonly preparation: readonly PreparationLineDto[];
}

export interface CreateOrderResponse {
  readonly order: OrderDto;
  readonly payment?: {
    readonly id: string;
    readonly checkoutUrl: string;
    readonly kind: 'immediate' | 'hold';
  };
  readonly disclosure?: {
    readonly penaltyLabel: string;
    readonly freeCancellationHours: number;
    readonly holdExplanation: string;
    readonly onlineExplanation: string;
  };
}

export interface ProducerFinanceDto {
  readonly carriedBalanceCents: number;
  readonly carriedSince: string | null;
  readonly payoutFrequency: 'monthly' | 'weekly';
  readonly subscriptionDueCents: number;
  readonly sepaEligible: boolean;
}

export interface CreateCheckoutBlockRequest {
  readonly items: readonly CreateOrderLineRequest[];
  readonly pickupPointId: string;
  readonly pickupSlotId: string;
  readonly pickupStartsAt: string;
}

export interface CreateCheckoutRequest {
  readonly blocks: readonly CreateCheckoutBlockRequest[];
  readonly paymentMethod: 'online' | 'onsite_cash' | 'onsite_card';
  readonly email?: string;
  readonly clientLatitude?: number;
  readonly clientLongitude?: number;
}

export interface CheckoutOrderSummaryDto {
  readonly id: string;
  readonly orderNumber: string;
  readonly producerProfileId: string;
  readonly producerName: string | null;
  readonly pickupPointId: string;
  readonly pickupPointLabel: string | null;
  readonly pickupCommune: string | null;
  readonly pickupAddress: string | null;
  readonly pickupSlotId: string;
  readonly pickupStartsAt: string;
  readonly pickupEndsAt: string;
  readonly status: string;
  readonly paymentMethod: string;
  readonly totalCents: number;
  readonly confirmToken: string;
  readonly pickedUpAt: string | null;
  readonly cancellationReason: string | null;
  readonly createdAt: string;
  readonly items: readonly OrderItemDto[];
  readonly providerPaymentId?: string | null;
}

export interface CheckoutDto {
  readonly id: string;
  readonly totalCents: number;
  readonly status: string;
  readonly paymentMethod: string;
  readonly orderCount: number;
  readonly producerNames: readonly string[];
  readonly summaryLabel: string;
  readonly orders: readonly CheckoutOrderSummaryDto[];
}

export interface CreateCheckoutResponse {
  readonly checkout: CheckoutDto;
  readonly payment?: {
    readonly id: string;
    readonly checkoutUrl: string;
    readonly kind: 'immediate' | 'hold';
  };
  readonly disclosure?: CreateOrderResponse['disclosure'];
}

export interface CreateGuestCheckoutResponse extends CreateCheckoutResponse {
  readonly guestAccessToken: string;
}

export interface CreateOrderLineRequest {
  readonly productId: string;
  readonly quantity: number;
  readonly extraIds?: readonly string[];
}

export interface CreateOrderRequest {
  readonly items: readonly CreateOrderLineRequest[];
  readonly pickupPointId: string;
  readonly pickupSlotId: string;
  readonly paymentMethod: 'online' | 'onsite_cash' | 'onsite_card';
  readonly email?: string;
  readonly clientLatitude?: number;
  readonly clientLongitude?: number;
  /** @deprecated Utiliser items[]. Conservé pour compatibilité ascendante côté API. */
  readonly productId?: string;
  readonly quantity?: number;
  readonly extraIds?: readonly string[];
}

export interface CreateGuestOrderResponse extends CreateOrderResponse {
  readonly guestAccessToken: string;
}

export interface PreparationLineDto {
  readonly productId: string;
  readonly productName: string;
  readonly priceType: string;
  readonly totalQuantity: number;
  readonly orderCount: number;
}

export interface PublicReviewDto {
  readonly id: string;
  readonly rating: number;
  readonly comment: string;
  readonly authorLabel: string;
  readonly experienceAt: string;
  readonly publishedAt: string;
  readonly producerReply: string | null;
  readonly producerReplyAt: string | null;
}

export interface ClientReviewDto {
  readonly id: string;
  readonly orderItemId: string;
  readonly orderId: string;
  readonly rating: number;
  readonly comment: string;
  readonly status: string;
  readonly rejectionReason: string | null;
  readonly experienceAt: string;
  readonly publishedAt: string | null;
  readonly producerReply: string | null;
  readonly producerReplyAt: string | null;
  readonly createdAt: string;
}

export interface ProducerReviewDto {
  readonly id: string;
  readonly productName: string;
  readonly rating: number;
  readonly comment: string;
  readonly status: string;
  readonly authorLabel: string;
  readonly experienceAt: string;
  readonly publishedAt: string | null;
  readonly producerReply: string | null;
  readonly producerReplyAt: string | null;
}

export interface ProducerStatsMonthlyDto {
  readonly month: string;
  readonly orderCount: number;
  readonly revenueCents: number;
  readonly unitsSold: number;
}

export interface ProducerStatsDto {
  readonly monthly: readonly ProducerStatsMonthlyDto[];
  readonly topProducts: readonly {
    readonly productId: string;
    readonly productName: string;
    readonly unitsSold: number;
    readonly revenueCents: number;
  }[];
  readonly peak?: { readonly label: string; readonly orderCount: number };
  readonly totalOrders: number;
  readonly totalRevenueCents: number;
}

export interface ShopTipDto {
  readonly id: string;
  readonly title: string;
  readonly body: string;
}

export interface ProducerMarginSettingsDto {
  readonly hourlyRateCents: number;
  readonly monthlyFixedCostsCents: number;
  readonly lossRatePercent: number;
  readonly transportCostCents: number;
  readonly categoryDefaults: Record<
    string,
    { readonly materialCostCents: number; readonly workMinutes: number; readonly packagingCostCents: number }
  >;
}

export interface ProducerProductMarginDto {
  readonly productId: string;
  readonly name: string;
  readonly category: string;
  readonly unitPriceCents: number;
  readonly materialCostCents: number;
  readonly workMinutes: number;
  readonly packagingCostCents: number;
  readonly grossMarginCents: number;
  readonly grossMarginPercent: number | null;
  readonly netMarginCents: number | null;
  readonly soldAtLoss: boolean;
  readonly suggestedPriceCents?: number;
  readonly costFloorCents?: number;
}

export interface ProducerAchievementDto {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly category: string;
  readonly unlocked: boolean;
  readonly unlockedAt: string | null;
}

/** Point d'entrée unique vers l'API : aucune URL n'est construite ailleurs. */
@Injectable({ providedIn: 'root' })
export class ApiClient {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(RUNTIME_CONFIG).apiUrl.replace(/\/$/, '');

  health(): Observable<HealthResponse> {
    return this.http.get<HealthResponse>(`${this.baseUrl}/health`);
  }

  /** Catalogue public pour l'écran de découverte, sans authentification. */
  catalog(): Observable<CatalogResponse> {
    return this.http.get<CatalogResponse>(`${this.baseUrl}/catalog`);
  }

  catalogBoutique(slug: string): Observable<CatalogBoutiqueResponse> {
    return this.http.get<CatalogBoutiqueResponse>(
      `${this.baseUrl}/catalog/boutique/${encodeURIComponent(slug)}`,
    );
  }

  meCatalog(): Observable<CatalogResponse> {
    return this.http.get<CatalogResponse>(`${this.baseUrl}/me/catalog`);
  }

  featured(latitude: number, longitude: number): Observable<{ readonly featured: FeaturedSlotDto | null }> {
    return this.http.get<{ readonly featured: FeaturedSlotDto | null }>(
      `${this.baseUrl}/catalog/vedette?lat=${encodeURIComponent(String(latitude))}&lng=${encodeURIComponent(String(longitude))}`,
    );
  }

  sponsored(latitude: number, longitude: number): Observable<{ readonly sponsored: FeaturedSlotDto | null }> {
    return this.http.get<{ readonly sponsored: FeaturedSlotDto | null }>(
      `${this.baseUrl}/catalog/sponsorise?lat=${encodeURIComponent(String(latitude))}&lng=${encodeURIComponent(String(longitude))}`,
    );
  }

  producerBoosters(): Observable<ProducerBoostersResponse> {
    return this.http.get<ProducerBoostersResponse>(`${this.baseUrl}/me/producteur/boosters`);
  }

  purchaseBooster(body: {
    readonly kind: BoosterKind;
    readonly productId?: string;
  }): Observable<{ readonly active: readonly ActiveBoosterDto[] }> {
    return this.http.post<{ readonly active: readonly ActiveBoosterDto[] }>(
      `${this.baseUrl}/me/producteur/boosters`,
      body,
    );
  }

  productDetail(producerId: string, productId: string): Observable<ProductDetailResponse> {
    return this.http.get<ProductDetailResponse>(
      `${this.baseUrl}/catalog/producteurs/${encodeURIComponent(producerId)}/produits/${encodeURIComponent(productId)}`,
    );
  }

  /** Fiche produit avec perks VIP (fenêtre anti-gaspi anticipée). */
  meProductDetail(producerId: string, productId: string): Observable<ProductDetailResponse> {
    return this.http.get<ProductDetailResponse>(
      `${this.baseUrl}/me/catalog/producteurs/${encodeURIComponent(producerId)}/produits/${encodeURIComponent(productId)}`,
    );
  }

  producerReviews(producerId: string): Observable<{ readonly reviews: readonly PublicReviewDto[] }> {
    return this.http.get<{ readonly reviews: readonly PublicReviewDto[] }>(
      `${this.baseUrl}/catalog/producteurs/${encodeURIComponent(producerId)}/avis`,
    );
  }

  productReviews(
    producerId: string,
    productId: string,
  ): Observable<{ readonly reviews: readonly PublicReviewDto[] }> {
    return this.http.get<{ readonly reviews: readonly PublicReviewDto[] }>(
      `${this.baseUrl}/catalog/producteurs/${encodeURIComponent(producerId)}/produits/${encodeURIComponent(productId)}/avis`,
    );
  }

  submitReview(
    orderId: string,
    itemId: string,
    body: { readonly rating: number; readonly comment: string },
  ): Observable<{ readonly review: ClientReviewDto }> {
    return this.http.post<{ readonly review: ClientReviewDto }>(
      `${this.baseUrl}/me/commandes/${encodeURIComponent(orderId)}/items/${encodeURIComponent(itemId)}/avis`,
      body,
    );
  }

  listProducerReviews(): Observable<{ readonly reviews: readonly ProducerReviewDto[] }> {
    return this.http.get<{ readonly reviews: readonly ProducerReviewDto[] }>(
      `${this.baseUrl}/me/producteur/avis`,
    );
  }

  listProducerAchievements(): Observable<{ readonly achievements: readonly ProducerAchievementDto[] }> {
    return this.http.get<{ readonly achievements: readonly ProducerAchievementDto[] }>(
      `${this.baseUrl}/me/producteur/succes`,
    );
  }

  producerStatistics(): Observable<{
    readonly stats: ProducerStatsDto;
    readonly tierLevel: number;
    readonly tips?: readonly ShopTipDto[];
  }> {
    return this.http.get<{
      readonly stats: ProducerStatsDto;
      readonly tierLevel: number;
      readonly tips?: readonly ShopTipDto[];
    }>(
      `${this.baseUrl}/me/producteur/statistiques`,
    );
  }

  producerMargin(): Observable<{
    readonly settings: ProducerMarginSettingsDto;
    readonly products: readonly ProducerProductMarginDto[];
    readonly tierLevel: number;
    readonly features: {
      readonly grossMargin: boolean;
      readonly priceComparison: boolean;
      readonly netMargin: boolean;
      readonly export: boolean;
      readonly suggestedPrice?: boolean;
    };
  }> {
    return this.http.get<{
      readonly settings: ProducerMarginSettingsDto;
      readonly products: readonly ProducerProductMarginDto[];
      readonly tierLevel: number;
      readonly features: {
        readonly grossMargin: boolean;
        readonly priceComparison: boolean;
        readonly netMargin: boolean;
        readonly export: boolean;
        readonly suggestedPrice?: boolean;
      };
    }>(`${this.baseUrl}/me/producteur/marge`);
  }

  updateProducerMarginSettings(body: {
    readonly hourlyRateCents: number;
    readonly monthlyFixedCostsCents: number;
    readonly lossRatePercent: number;
    readonly transportCostCents: number;
  }): Observable<{ readonly settings: ProducerMarginSettingsDto }> {
    return this.http.put<{ readonly settings: ProducerMarginSettingsDto }>(
      `${this.baseUrl}/me/producteur/marge/reglages`,
      body,
    );
  }

  producerPriceComparison(): Observable<{
    readonly benchmarks: readonly {
      readonly category: string;
      readonly priceType: string;
      readonly sampleSize: number;
      readonly minPriceCents: number;
      readonly medianPriceCents: number;
      readonly maxPriceCents: number;
      readonly yourPriceCents: number;
    }[];
  }> {
    return this.http.get<{
      readonly benchmarks: readonly {
        readonly category: string;
        readonly priceType: string;
        readonly sampleSize: number;
        readonly minPriceCents: number;
        readonly medianPriceCents: number;
        readonly maxPriceCents: number;
        readonly yourPriceCents: number;
        readonly reading?: string;
      }[];
    }>(`${this.baseUrl}/me/producteur/marge/comparaison`);
  }

  producerMarginExport(): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/me/producteur/marge/export`, { responseType: 'blob' });
  }

  replyToReview(
    reviewId: string,
    reply: string,
  ): Observable<{ readonly review: { readonly id: string; readonly producerReply: string | null } }> {
    return this.http.post<{ readonly review: { readonly id: string; readonly producerReply: string | null } }>(
      `${this.baseUrl}/me/producteur/avis/${encodeURIComponent(reviewId)}/reponse`,
      { reply },
    );
  }

  listOrders(): Observable<{ readonly orders: readonly OrderDto[] }> {
    return this.http.get<{ readonly orders: readonly OrderDto[] }>(`${this.baseUrl}/me/commandes`);
  }

  createOrder(body: CreateOrderRequest): Observable<CreateOrderResponse> {
    return this.http.post<CreateOrderResponse>(`${this.baseUrl}/me/commandes`, body);
  }

  createCheckout(body: CreateCheckoutRequest): Observable<CreateCheckoutResponse> {
    return this.http.post<CreateCheckoutResponse>(`${this.baseUrl}/me/checkouts`, body);
  }

  createGuestCheckout(body: CreateCheckoutRequest & { readonly email: string }): Observable<CreateGuestCheckoutResponse> {
    return this.http.post<CreateGuestCheckoutResponse>(`${this.baseUrl}/checkouts`, body);
  }

  confirmCheckoutPayment(checkoutId: string): Observable<{ readonly checkout: CheckoutDto }> {
    return this.http.post<{ readonly checkout: CheckoutDto }>(
      `${this.baseUrl}/me/checkouts/${encodeURIComponent(checkoutId)}/paiement/confirmer`,
      {},
    );
  }

  confirmGuestCheckoutPayment(checkoutId: string, token: string): Observable<{ readonly checkout: CheckoutDto }> {
    const params = new URLSearchParams({ jeton: token });
    return this.http.post<{ readonly checkout: CheckoutDto }>(
      `${this.baseUrl}/checkouts/${encodeURIComponent(checkoutId)}/paiement/confirmer?${params.toString()}`,
      {},
    );
  }

  createGuestOrder(body: CreateOrderRequest & { readonly email: string }): Observable<CreateGuestOrderResponse> {
    return this.http.post<CreateGuestOrderResponse>(`${this.baseUrl}/commandes`, body);
  }

  getGuestOrder(orderId: string, token: string): Observable<{ readonly order: OrderDto }> {
    const params = new URLSearchParams({ jeton: token });
    return this.http.get<{ readonly order: OrderDto }>(
      `${this.baseUrl}/commandes/${encodeURIComponent(orderId)}?${params.toString()}`,
    );
  }

  confirmGuestOrderPayment(orderId: string, token: string): Observable<{ readonly order: OrderDto }> {
    const params = new URLSearchParams({ jeton: token });
    return this.http.post<{ readonly order: OrderDto }>(
      `${this.baseUrl}/commandes/${encodeURIComponent(orderId)}/paiement/confirmer?${params.toString()}`,
      {},
    );
  }

  confirmOrderPayment(orderId: string): Observable<{ readonly order: OrderDto }> {
    return this.http.post<{ readonly order: OrderDto }>(
      `${this.baseUrl}/me/commandes/${encodeURIComponent(orderId)}/paiement/confirmer`,
      {},
    );
  }

  producerFinance(): Observable<{ readonly finance: ProducerFinanceDto }> {
    return this.http.get<{ readonly finance: ProducerFinanceDto }>(
      `${this.baseUrl}/me/producteur/finance`,
    );
  }

  reportNoShow(orderId: string): Observable<{ readonly order: OrderDto | null }> {
    return this.http.post<{ readonly order: OrderDto | null }>(
      `${this.baseUrl}/me/producteur/commandes/${encodeURIComponent(orderId)}/non-retire`,
      {},
    );
  }

  listProducerOrders(): Observable<{ readonly orders: readonly OrderDto[] }> {
    return this.http.get<{ readonly orders: readonly OrderDto[] }>(
      `${this.baseUrl}/me/producteur/commandes`,
    );
  }

  listPickupSessions(): Observable<{ readonly sessions: readonly PickupSessionSummaryDto[] }> {
    return this.http.get<{ readonly sessions: readonly PickupSessionSummaryDto[] }>(
      `${this.baseUrl}/me/producteur/retraits/sessions`,
    );
  }

  getPickupSession(query: {
    readonly pickupPointId: string;
    readonly pickupSlotId: string;
    readonly pickupStartsAt: string;
  }): Observable<PickupSessionDetailDto> {
    const params = new URLSearchParams({
      pickupPointId: query.pickupPointId,
      pickupSlotId: query.pickupSlotId,
      pickupStartsAt: query.pickupStartsAt,
    });
    return this.http.get<PickupSessionDetailDto>(
      `${this.baseUrl}/me/producteur/retraits/session?${params.toString()}`,
    );
  }

  scanPickup(confirmToken: string): Observable<{ readonly order: OrderDto }> {
    return this.http.post<{ readonly order: OrderDto }>(
      `${this.baseUrl}/me/producteur/retraits/scan`,
      { confirmToken },
    );
  }

  downloadOrderConfirmationPdf(orderId: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/me/commandes/${encodeURIComponent(orderId)}/confirmation.pdf`, {
      responseType: 'blob',
    });
  }

  downloadGuestOrderConfirmationPdf(orderId: string, token: string): Observable<Blob> {
    const params = new URLSearchParams({ jeton: token });
    return this.http.get(
      `${this.baseUrl}/commandes/${encodeURIComponent(orderId)}/confirmation.pdf?${params.toString()}`,
      { responseType: 'blob' },
    );
  }

  confirmPickup(orderId: string, confirmToken?: string): Observable<{ readonly order: OrderDto }> {
    return this.http.post<{ readonly order: OrderDto }>(
      `${this.baseUrl}/me/producteur/commandes/${encodeURIComponent(orderId)}/retrait`,
      confirmToken ? { confirmToken } : {},
    );
  }

  me(): Observable<MeResponse> {
    return this.http.get<MeResponse>(`${this.baseUrl}/me`);
  }

  updateMe(body: MeUpdate): Observable<MeResponse> {
    return this.http.put<MeResponse>(`${this.baseUrl}/me`, body);
  }

  clientAchievements(): Observable<ClientAchievementsResponse> {
    return this.http.get<ClientAchievementsResponse>(`${this.baseUrl}/me/succes`);
  }

  clientFavorites(): Observable<ClientFavoritesResponse> {
    return this.http.get<ClientFavoritesResponse>(`${this.baseUrl}/me/favoris`);
  }

  addClientFavoriteProducer(producerId: string): Observable<{ readonly added: boolean }> {
    return this.http.post<{ readonly added: boolean }>(
      `${this.baseUrl}/me/favoris/producteurs/${encodeURIComponent(producerId)}`,
      {},
    );
  }

  removeClientFavoriteProducer(producerId: string): Observable<{ readonly removed: boolean }> {
    return this.http.delete<{ readonly removed: boolean }>(
      `${this.baseUrl}/me/favoris/producteurs/${encodeURIComponent(producerId)}`,
    );
  }

  addClientFavoriteProduct(productId: string): Observable<{ readonly added: boolean }> {
    return this.http.post<{ readonly added: boolean }>(
      `${this.baseUrl}/me/favoris/produits/${encodeURIComponent(productId)}`,
      {},
    );
  }

  removeClientFavoriteProduct(productId: string): Observable<{ readonly removed: boolean }> {
    return this.http.delete<{ readonly removed: boolean }>(
      `${this.baseUrl}/me/favoris/produits/${encodeURIComponent(productId)}`,
    );
  }

  getActiveCart(): Observable<{ readonly cart: CartPayloadDto | null; readonly updatedAt: string | null }> {
    return this.http.get<{ readonly cart: CartPayloadDto | null; readonly updatedAt: string | null }>(
      `${this.baseUrl}/me/panier`,
    );
  }

  putActiveCart(cart: CartPayloadDto): Observable<{ readonly cart: CartPayloadDto; readonly updatedAt: string }> {
    return this.http.put<{ readonly cart: CartPayloadDto; readonly updatedAt: string }>(
      `${this.baseUrl}/me/panier`,
      { cart },
    );
  }

  clearActiveCart(): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/me/panier`, { cart: null });
  }

  listSavedCartTemplates(): Observable<{ readonly templates: readonly SavedCartTemplateSummaryDto[] }> {
    return this.http.get<{ readonly templates: readonly SavedCartTemplateSummaryDto[] }>(
      `${this.baseUrl}/me/panier/modeles`,
    );
  }

  saveCartTemplate(body: {
    readonly cart: CartPayloadDto;
    readonly label?: string;
    readonly templateId?: string;
  }): Observable<{ readonly saved: boolean; readonly updated: boolean }> {
    return this.http.post<{ readonly saved: boolean; readonly updated: boolean }>(
      `${this.baseUrl}/me/panier/modeles`,
      body,
    );
  }

  getSavedCartTemplate(
    templateId: string,
  ): Observable<{ readonly template: SavedCartTemplateDetailDto }> {
    return this.http.get<{ readonly template: SavedCartTemplateDetailDto }>(
      `${this.baseUrl}/me/panier/modeles/${encodeURIComponent(templateId)}`,
    );
  }

  deleteSavedCartTemplate(templateId: string): Observable<{ readonly removed: boolean }> {
    return this.http.delete<{ readonly removed: boolean }>(
      `${this.baseUrl}/me/panier/modeles/${encodeURIComponent(templateId)}`,
    );
  }

  saveGuestCart(body: {
    readonly email: string;
    readonly cart: CartPayloadDto;
  }): Observable<{ readonly snapshotId: string; readonly guestAccessToken: string; readonly cartUrl: string }> {
    return this.http.post<{ readonly snapshotId: string; readonly guestAccessToken: string; readonly cartUrl: string }>(
      `${this.baseUrl}/panier/sauvegarder`,
      body,
    );
  }

  getGuestCart(snapshotId: string, token: string): Observable<{ readonly cart: CartPayloadDto }> {
    const params = new URLSearchParams({ jeton: token });
    return this.http.get<{ readonly cart: CartPayloadDto }>(
      `${this.baseUrl}/panier/sauvegardes/${encodeURIComponent(snapshotId)}?${params.toString()}`,
    );
  }

  clientImpact(): Observable<{ readonly impact: PersonalImpactDto }> {
    return this.http.get<{ readonly impact: PersonalImpactDto }>(`${this.baseUrl}/me/impact`);
  }

  notificationSettings(): Observable<NotificationSettings> {
    return this.http.get<NotificationSettings>(`${this.baseUrl}/me/notifications`);
  }

  updateNotificationSettings(body: NotificationSettings): Observable<NotificationSettings> {
    return this.http.put<NotificationSettings>(`${this.baseUrl}/me/notifications`, body);
  }

  /**
   * Consulte le répertoire Sirene via notre backend.
   *
   * L'annuaire de la DINUM accepterait un appel direct du navigateur, mais la
   * même consultation sert de contrôle au moment de créer le profil : une seule
   * implémentation, un seul jeu de messages.
   */
  lookupSiret(siret: string): Observable<SireneLookupResponse> {
    return this.http.get<SireneLookupResponse>(
      `${this.baseUrl}/siret/${encodeURIComponent(siret)}`,
    );
  }

  producerProfile(): Observable<ProducerProfileResponse> {
    return this.http.get<ProducerProfileResponse>(`${this.baseUrl}/me/producteur`);
  }

  producerTier(): Observable<{ readonly tier: ProducerTierDto }> {
    return this.http.get<{ readonly tier: ProducerTierDto }>(`${this.baseUrl}/me/producteur/palier`);
  }

  producerShop(): Observable<ProducerShopResponse> {
    return this.http.get<ProducerShopResponse>(`${this.baseUrl}/me/producteur/boutique`);
  }

  saveProducerShop(body: ProducerShopRequest): Observable<ProducerShopResponse> {
    return this.http.put<ProducerShopResponse>(`${this.baseUrl}/me/producteur/boutique`, body);
  }

  producerPoster(): Observable<ProducerPosterResponse> {
    return this.http.get<ProducerPosterResponse>(`${this.baseUrl}/me/producteur/affiche`);
  }

  saveProducerPoster(body: ProducerPosterRequest): Observable<ProducerPosterResponse> {
    return this.http.put<ProducerPosterResponse>(`${this.baseUrl}/me/producteur/affiche`, body);
  }

  downgradeProducerTier(level: number): Observable<{ readonly tier: ProducerTierDto }> {
    return this.http.post<{ readonly tier: ProducerTierDto }>(
      `${this.baseUrl}/me/producteur/palier/descente`,
      { level },
    );
  }

  cancelProducerTierDowngrade(): Observable<{ readonly tier: ProducerTierDto }> {
    return this.http.post<{ readonly tier: ProducerTierDto }>(
      `${this.baseUrl}/me/producteur/palier/descente/annuler`,
      {},
    );
  }

  boostProducerTier(level: number): Observable<{ readonly tier: ProducerTierDto }> {
    return this.http.post<{ readonly tier: ProducerTierDto }>(
      `${this.baseUrl}/me/producteur/palier/boost`,
      { level },
    );
  }

  saveProducerProfile(body: ProducerOnboardingRequest): Observable<ProducerProfileResponse> {
    return this.http.post<ProducerProfileResponse>(`${this.baseUrl}/me/producteur`, body);
  }

  startProducerPayment(): Observable<ProducerPaymentResponse> {
    return this.http.post<ProducerPaymentResponse>(`${this.baseUrl}/me/producteur/paiement`, {});
  }

  pickupPoints(): Observable<PickupPointsResponse> {
    return this.http.get<PickupPointsResponse>(`${this.baseUrl}/me/producteur/points-de-retrait`);
  }

  createPickupPoint(body: PickupPointRequest): Observable<PickupPointResponse> {
    return this.http.post<PickupPointResponse>(
      `${this.baseUrl}/me/producteur/points-de-retrait`,
      body,
    );
  }

  /** Un point et ses créneaux se remplacent en bloc : l'écran les édite comme un tout. */
  updatePickupPoint(id: string, body: PickupPointRequest): Observable<PickupPointResponse> {
    return this.http.put<PickupPointResponse>(
      `${this.baseUrl}/me/producteur/points-de-retrait/${encodeURIComponent(id)}`,
      body,
    );
  }

  deletePickupPoint(id: string): Observable<{ readonly deleted: boolean }> {
    return this.http.delete<{ readonly deleted: boolean }>(
      `${this.baseUrl}/me/producteur/points-de-retrait/${encodeURIComponent(id)}`,
    );
  }

  producerTeam(): Observable<ProducerTeamResponse> {
    return this.http.get<ProducerTeamResponse>(`${this.baseUrl}/me/producteur/equipe`);
  }

  inviteStaff(
    body: InviteStaffRequest,
  ): Observable<{ readonly member: StaffMemberDto; readonly team: ProducerTeamResponse }> {
    return this.http.post<{ readonly member: StaffMemberDto; readonly team: ProducerTeamResponse }>(
      `${this.baseUrl}/me/producteur/equipe`,
      body,
    );
  }

  setStaffRole(
    userId: string,
    role: StaffRole,
  ): Observable<{ readonly team: ProducerTeamResponse }> {
    return this.http.patch<{ readonly team: ProducerTeamResponse }>(
      `${this.baseUrl}/me/producteur/equipe/${encodeURIComponent(userId)}`,
      { role },
    );
  }

  revokeStaff(userId: string): Observable<{ readonly team: ProducerTeamResponse }> {
    return this.http.delete<{ readonly team: ProducerTeamResponse }>(
      `${this.baseUrl}/me/producteur/equipe/${encodeURIComponent(userId)}`,
    );
  }

  respondToInvitation(
    profileId: string,
    accept: boolean,
  ): Observable<{ readonly accepted: boolean }> {
    return this.http.post<{ readonly accepted: boolean }>(
      `${this.baseUrl}/me/producteur/invitations`,
      { profileId, accept },
    );
  }

  products(): Observable<ProductsResponse> {
    return this.http.get<ProductsResponse>(`${this.baseUrl}/me/producteur/produits`);
  }

  createProduct(body: ProductRequest): Observable<ProductResponse> {
    return this.http.post<ProductResponse>(`${this.baseUrl}/me/producteur/produits`, body);
  }

  updateProduct(id: string, body: ProductRequest): Observable<ProductResponse> {
    return this.http.put<ProductResponse>(
      `${this.baseUrl}/me/producteur/produits/${encodeURIComponent(id)}`,
      body,
    );
  }

  archiveProduct(id: string): Observable<{ readonly archived: boolean }> {
    return this.http.delete<{ readonly archived: boolean }>(
      `${this.baseUrl}/me/producteur/produits/${encodeURIComponent(id)}`,
    );
  }

  productAvailabilities(productId: string): Observable<ProductAvailabilitiesResponse> {
    return this.http.get<ProductAvailabilitiesResponse>(
      `${this.baseUrl}/me/producteur/produits/${encodeURIComponent(productId)}/disponibilites`,
    );
  }

  upsertProductAvailability(
    productId: string,
    body: { readonly occurrenceId: string; readonly quantity: number },
  ): Observable<UpsertProductAvailabilityResponse> {
    return this.http.put<UpsertProductAvailabilityResponse>(
      `${this.baseUrl}/me/producteur/produits/${encodeURIComponent(productId)}/disponibilites`,
      body,
    );
  }

  /** Demande une URL présignée S3 pour téléverser une photo produit. */
  requestMediaUpload(contentType: string): Observable<MediaUploadResponse> {
    return this.http.post<MediaUploadResponse>(`${this.baseUrl}/me/producteur/medias`, {
      contentType,
    });
  }

  /** Présignature pour la photo de profil client. */
  requestClientMediaUpload(contentType: string): Observable<MediaUploadResponse> {
    return this.http.post<MediaUploadResponse>(`${this.baseUrl}/me/medias`, {
      contentType,
    });
  }

  adminOverview(period: AdminPeriodId): Observable<{ readonly overview: AdminOverviewDto }> {
    return this.http.get<{ readonly overview: AdminOverviewDto }>(
      `${this.baseUrl}/admin/tableau-de-bord/globale?periode=${period}`,
    );
  }

  adminAlerts(): Observable<{ readonly alerts: AdminAlertsDto }> {
    return this.http.get<{ readonly alerts: AdminAlertsDto }>(`${this.baseUrl}/admin/alertes`);
  }

  adminCatalog(period: AdminPeriodId): Observable<{ readonly catalog: AdminCatalogDto }> {
    return this.http.get<{ readonly catalog: AdminCatalogDto }>(
      `${this.baseUrl}/admin/catalogue?periode=${period}`,
    );
  }

  adminProducers(params: {
    readonly period: AdminPeriodId;
    readonly status?: string;
    readonly tier?: number;
    readonly sector?: string;
    readonly search?: string;
    readonly sort?: string;
    readonly onlyIssues?: boolean;
  }): Observable<{
    readonly producers: readonly AdminProducerSummaryDto[];
    readonly impersonationEnabled: boolean;
  }> {
    const search = new URLSearchParams({ periode: params.period });
    if (params.status) search.set('status', params.status);
    if (params.tier !== undefined) search.set('tier', String(params.tier));
    if (params.sector) search.set('sector', params.sector);
    if (params.search) search.set('q', params.search);
    if (params.sort) search.set('tri', params.sort);
    if (params.onlyIssues === true) search.set('anomalies', '1');
    return this.http.get<{
      readonly producers: readonly AdminProducerSummaryDto[];
      readonly impersonationEnabled: boolean;
    }>(`${this.baseUrl}/admin/producteurs?${search.toString()}`);
  }

  adminProducerDetail(
    producerId: string,
    period: AdminPeriodId,
  ): Observable<{ readonly producer: AdminProducerDetailDto }> {
    return this.http.get<{ readonly producer: AdminProducerDetailDto }>(
      `${this.baseUrl}/admin/producteurs/${encodeURIComponent(producerId)}?periode=${period}`,
    );
  }

  adminClients(params: {
    readonly period: AdminPeriodId;
    readonly search?: string;
    readonly segment?: string;
    readonly sort?: string;
  }): Observable<AdminClientsViewDto> {
    const search = new URLSearchParams({ periode: params.period });
    if (params.search) search.set('q', params.search);
    if (params.segment) search.set('segment', params.segment);
    if (params.sort) search.set('tri', params.sort);
    return this.http.get<AdminClientsViewDto>(
      `${this.baseUrl}/admin/clients?${search.toString()}`,
    );
  }

  adminClientDetail(
    userId: string,
    period: AdminPeriodId,
  ): Observable<{ readonly client: AdminClientDetailDto }> {
    return this.http.get<{ readonly client: AdminClientDetailDto }>(
      `${this.baseUrl}/admin/clients/${encodeURIComponent(userId)}?periode=${period}`,
    );
  }

  adminStrategy(): Observable<{ readonly strategy: AdminStrategyDto }> {
    return this.http.get<{ readonly strategy: AdminStrategyDto }>(`${this.baseUrl}/admin/strategie`);
  }

  adminDistance(): Observable<{ readonly distance: AdminDistanceDto }> {
    return this.http.get<{ readonly distance: AdminDistanceDto }>(`${this.baseUrl}/admin/distance`);
  }

  adminClusters(): Observable<{ readonly clusters: readonly AdminClusterDto[] }> {
    return this.http.get<{ readonly clusters: readonly AdminClusterDto[] }>(
      `${this.baseUrl}/admin/mutualisation`,
    );
  }

  startAdminImpersonation(producerProfileId: string): Observable<{ readonly ok: boolean }> {
    return this.http.post<{ readonly ok: boolean }>(`${this.baseUrl}/admin/impersonation`, {
      producerProfileId,
    });
  }

  stopAdminImpersonation(): Observable<{ readonly ok: boolean }> {
    return this.http.delete<{ readonly ok: boolean }>(`${this.baseUrl}/admin/impersonation`);
  }

  logCatalogSearch(body: {
    readonly query: string;
    readonly sector: string | null;
    readonly resultsCount: number;
  }): Observable<{ readonly ok: boolean }> {
    return this.http.post<{ readonly ok: boolean }>(`${this.baseUrl}/catalog/recherche-journal`, body);
  }
}
